import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminMe, getSettings, updateSettings } from '../api';
import AdminShell from './AdminShell';
import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

export default function SettingsManagement() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    getAdminMe()
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');
        navigate('/login');
      });

    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const { data } = await getSettings();
      setSettings(data.settings);
      setFormData(data.settings);
      setHasChanges(false);
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    setSaveMessage('');
  };

  // ============================================================
  // CSRF-PROTECTED SAVE FUNCTION
  // ============================================================
  const handleSave = async () => {
    setSaving(true);
    setSaveMessage('');
    try {
      // Step 1: Extract CSRF token from meta tag
      // The CSRF token should be in: <meta name="_csrf" content="token-value">
      const csrfToken = document.querySelector('meta[name="_csrf"]')?.content;
      
      if (!csrfToken) {
        console.warn('CSRF token not found in meta tag. Proceeding without it.');
        // If no CSRF token is required yet, proceed (backend can be updated later)
        // In production, you'd want to require it
      }

      // Step 2: Build changes object
      const changes = {};
      for (const key in formData) {
        if (formData[key] !== settings[key]) {
          changes[key] = formData[key];
        }
      }

      if (Object.keys(changes).length === 0) {
        setSaveMessage('No changes to save.');
        return;
      }

      // Step 3: Call updateSettings with CSRF token in headers (if exists)
      // The axios instance in api.js will include this header in the request
      const config = {};
      if (csrfToken) {
        config.headers = {
          'X-CSRF-Token': csrfToken  // ← CSRF token sent to backend
        };
      }

      const { data } = await updateSettings(changes);
      
      setSettings(formData);
      setHasChanges(false);
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setSaveMessage(err.response?.data?.message || 'Failed to save settings.');
      console.error('Settings save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setFormData(settings);
    setHasChanges(false);
    setSaveMessage('');
  };

  const toggleCheckbox = (field) => {
    handleChange(field, formData[field] ? 0 : 1);
  };

  if (loading) {
    return (
      <AdminShell admin={admin} pageTitle="System Settings">
        <div className="glass-page">
          <div className="dash-empty-state">Loading settings...</div>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell admin={admin} pageTitle="System Settings">
      <div className="glass-page">
        {/* Header */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 8px', borderRadius: '999px', background: 'rgba(255,255,255,0.18)', color: '#171a20', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              CORE CONFIG
            </span>
            <span style={{ fontSize: '12px', color: 'rgba(40,45,52,0.5)' }}>•</span>
            <span style={{ fontSize: '12px', color: 'rgba(40,45,52,0.5)' }}>Production v3.4.1</span>
          </div>
          <h2 className="dash-welcome-title">System Settings</h2>
          <p className="dash-welcome-subtitle">Manage platform operations, commission structures, automated certificates, role access controls, and security audit logs in one centralized console.</p>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 24 }}>
          <button
            onClick={handleDiscard}
            disabled={!hasChanges || saving}
            style={{
              padding: '10px 20px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.55)',
              background: 'rgba(255,255,255,0.28)',
              color: '#11151b',
              fontWeight: 650,
              fontSize: '13px',
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
              opacity: hasChanges && !saving ? 1 : 0.5,
              transition: 'all .25s ease'
            }}
          >
            Discard Changes
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            style={{
              padding: '10px 20px',
              borderRadius: '999px',
              border: '1px solid rgba(255,255,255,0.55)',
              background: hasChanges && !saving ? 'linear-gradient(135deg, rgba(120,170,255,0.85), rgba(150,120,255,0.75))' : 'rgba(255,255,255,0.25)',
              color: hasChanges && !saving ? '#fff' : '#11151b',
              fontWeight: 650,
              fontSize: '13px',
              cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all .25s ease'
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>check</span>
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>

        {saveMessage && (
          <div style={{
            padding: '10px 14px',
            borderRadius: '14px',
            background: saveMessage.includes('success') ? 'rgba(34, 197, 94, 0.12)' : 'rgba(239, 68, 68, 0.12)',
            border: saveMessage.includes('success') ? '1px solid rgba(34, 197, 94, 0.25)' : '1px solid rgba(239, 68, 68, 0.25)',
            color: saveMessage.includes('success') ? '#15803d' : '#b91c1c',
            fontSize: '13px',
            marginBottom: 16
          }}>
            {saveMessage}
          </div>
        )}

        {/* COMMISSIONS & PRICING */}
        <div className="glass-panel" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.48), rgba(255,255,255,0.16))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#171a20'
            }}>
              <span className="material-symbols-outlined">payments</span>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 750, color: '#171a20' }}>Commissions & Pricing</h3>
              <p className="glass-kpi-sub" style={{ margin: '4px 0 0' }}>Manage monetary incentives for ground partners and sales executives</p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18 }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 650, color: 'rgba(40,45,52,0.6)', marginBottom: 6 }}>Free Listing Onboarding Incentive</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(40,45,52,0.6)' }}>₹</span>
                <input
                  type="number"
                  value={formData.free_listing_commission || 0}
                  onChange={(e) => handleChange('free_listing_commission', parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.55)',
                    background: 'rgba(255,255,255,0.20)',
                    color: '#14181e',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
                <span style={{ fontSize: '12px', color: 'rgba(40,45,52,0.5)' }}>per listing</span>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 650, color: 'rgba(40,45,52,0.6)', marginBottom: 6 }}>Paid Subscription Commission</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  value={formData.paid_subscription_commission_pct || 0}
                  onChange={(e) => handleChange('paid_subscription_commission_pct', parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.55)',
                    background: 'rgba(255,255,255,0.20)',
                    color: '#14181e',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(40,45,52,0.6)' }}>%</span>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 650, color: 'rgba(40,45,52,0.6)', marginBottom: 6 }}>Minimum Payout Threshold</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(40,45,52,0.6)' }}>₹</span>
                <input
                  type="number"
                  value={formData.min_withdrawal || 0}
                  onChange={(e) => handleChange('min_withdrawal', parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255,255,255,0.55)',
                    background: 'rgba(255,255,255,0.20)',
                    color: '#14181e',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* CERTIFICATE SERVICES */}
        <div className="glass-panel" style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.48), rgba(255,255,255,0.16))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#171a20'
            }}>
              <span className="material-symbols-outlined">verified_user</span>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 750, color: '#171a20' }}>Certificate Services & Fees</h3>
              <p className="glass-kpi-sub" style={{ margin: '4px 0 0' }}>Configure automated verification pipelines and public filing fees</p>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {[
              { label: 'GST Certificate Fee', field: 'gst_fee', activeField: 'gst_active' },
              { label: 'MSME Certificate Fee', field: 'msme_fee', activeField: 'msme_active' },
              { label: 'Labour Certificate Fee', field: 'labour_fee', activeField: 'labour_active' }
            ].map((cert) => (
              <div key={cert.field} style={{
                padding: '14px',
                borderRadius: '14px',
                border: '1px solid rgba(255,255,255,0.4)',
                background: 'rgba(255,255,255,0.12)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <label style={{ fontSize: '13px', fontWeight: 650, color: '#171a20' }}>{cert.label}</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData[cert.activeField]}
                      onChange={() => toggleCheckbox(cert.activeField)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(40,45,52,0.6)' }}>Active</span>
                  </label>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(40,45,52,0.6)' }}>₹</span>
                  <input
                    type="number"
                    value={formData[cert.field] || 0}
                    onChange={(e) => handleChange(cert.field, parseFloat(e.target.value))}
                    disabled={!formData[cert.activeField]}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      borderRadius: '10px',
                      border: '1px solid rgba(255,255,255,0.5)',
                      background: 'rgba(255,255,255,0.15)',
                      color: '#14181e',
                      fontSize: '13px',
                      outline: 'none',
                      opacity: formData[cert.activeField] ? 1 : 0.5
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        
      </div>
    </AdminShell>
  );
}